import { notify } from '@eleveo/components-react';
import { AxiosInstance } from 'axios';
import { useQuery, UseQueryOptions } from 'react-query';

import { ErrorHandlerFunction } from './types';
import { useDataProvider } from '../app/DataProvider';
import { ETL_INTEGRATION_TYPE, Values } from '../components/configuration/types';
import { TeamOption } from '../components/forms/teamselector/types';
import { EndpointsFactory, WebexCallingTeamsRequestBodyType } from '../constants/integrationConstants';

export const INTEGRATION_TEAMS_ROOT_KEY = '-integration-teams';

export const handleIntegrationTeamsErrors: ErrorHandlerFunction = (queryKey, t) => {
  if (Array.isArray(queryKey)) {
    const isIntegrationTeamsError = queryKey[0].includes(INTEGRATION_TEAMS_ROOT_KEY);

    if (isIntegrationTeamsError) {
      notify.error(
        t('myConfigurations.teamSelector.response.failedIntegrationTeams', {
          providerName: t(`providers.${String(queryKey[0]).replace(INTEGRATION_TEAMS_ROOT_KEY, '')}`),
        })
      );
    }

    return isIntegrationTeamsError;
  }

  return false;
};

export const createIntegrationTeamsKey = (integration: Values<typeof ETL_INTEGRATION_TYPE>) =>
  `${integration}${INTEGRATION_TEAMS_ROOT_KEY}` as const;

const getCorrectFetchFunction = (
  client: AxiosInstance,
  integration: Values<typeof ETL_INTEGRATION_TYPE>,
  data: WebexCallingTeamsRequestBodyType
): (() => Promise<TeamOption[]>) => {
  switch (integration) {
    case ETL_INTEGRATION_TYPE.wxcalling:
      return () => EndpointsFactory.getWebexCallingTeams(client, data);
    default:
      throw new Error('Received invalid integration!');
  }
};

export function useIntegrationTeamsQuery(
  integrationToRequest: Values<typeof ETL_INTEGRATION_TYPE>,
  { accessToken, apiUrl }: WebexCallingTeamsRequestBodyType = {
    accessToken: '',
    apiUrl: '',
  },
  options: UseQueryOptions<
    TeamOption[],
    Error,
    TeamOption[],
    [
      ReturnType<typeof createIntegrationTeamsKey>,
      WebexCallingTeamsRequestBodyType['accessToken'],
      WebexCallingTeamsRequestBodyType['apiUrl']
    ]
  > = {}
) {
  const { client } = useDataProvider();
  const isEnabled = !!(accessToken && apiUrl);

  return useQuery(
    [createIntegrationTeamsKey(integrationToRequest), accessToken, apiUrl],
    getCorrectFetchFunction(client, integrationToRequest, { accessToken, apiUrl }),
    {
      ...options,
      // query consumer should handle permissions
      enabled: isEnabled && options.enabled,
    }
  );
}
